import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '../components/ui/Card';
import { AI_AGENTS_DATA } from '../data/agents';
import ConfirmationDialog from '../components/ui/ConfirmationDialog';
import { agentService } from "../services/agentService";
import { toast } from 'react-toastify';
import { ManifestEditor } from '../components/ManifestEditor';

type CustomAgentType = {
  type: string; 
  description?: string;
  code: string;
  name: string;
  active: boolean;
  capabilities: string[];
  policies: string[];
  metadata?: any;
};

type AgentServer = {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  isActive: boolean;
  metadata?: any;
  capabilities: any[];
  policies: any[];
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
};

type PaginatedAgents = {
  agentTypes: AgentServer[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};


const toCodeSuggestion = (name: string) =>
  name.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 40);


// Server DTO shapes (for clarity; adapt if your backend differs)
type ServerCapability = { capabilityCode: string; config: Record<string, any> };
type ServerPolicy = { policyCode: string; rules: Record<string, any> };

type CreateAgentDto = {
  code: string;
  name: string;
  description?: string;
  isActive: boolean;
  metadata?: Record<string, any>;
  capabilities: ServerCapability[];
  policies: ServerPolicy[];
};

type UpdateAgentDto = Partial<CreateAgentDto>;

// Safely parse JSON after a ":" suffix (e.g. sentiment:{"threshold":0.7})
const parseSuffixJSON = (raw: string) => {
  const idx = raw.indexOf(":");
  if (idx === -1) return { key: raw.trim(), obj: {} };
  const key = raw.slice(0, idx).trim();
  const maybeJson = raw.slice(idx + 1).trim();
  try {
    const obj = maybeJson ? JSON.parse(maybeJson) : {};
    return { key, obj };
  } catch {
    console.warn("Invalid JSON after ':' in", raw, "— sending empty config/rules");
    return { key, obj: {} };
  }
};

// Map UI fields → server payload
const toServerPayload = ({
  code,
  name,
  desc,
  active,
  capabilities,
  policies,
  metadataObj,
}: {
  code: string;
  name: string;
  desc?: string;
  active: boolean;
  capabilities: string[];
  policies: string[];
  metadataObj?: any;
}): CreateAgentDto => {
  const caps: ServerCapability[] = capabilities.map((c) => {
    const { key, obj } = parseSuffixJSON(c);
    return { capabilityCode: key, config: obj };
  });
  const pols: ServerPolicy[] = policies.map((p) => {
    const { key, obj } = parseSuffixJSON(p);
    return { policyCode: key, rules: obj };
  });

  return {
    code,
    name,
    description: desc || undefined,
    isActive: active,
    metadata: metadataObj || undefined,
    capabilities: caps,
    policies: pols,
  };
};


export const AgentTypes: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newTypeCode, setNewTypeCode] = useState('');
  const [newTypeName, setNewTypeName] = useState('');
  const [newTypeDescription, setNewTypeDescription] = useState('');
  const [newTypeActive, setNewTypeActive] = useState(true);
  const [newTypeCapabilities, setNewTypeCapabilities] = useState<string[]>([]);
  const [newTypePolicies, setNewTypePolicies] = useState<string[]>([]);
  const [newTypeMetadata, setNewTypeMetadata] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [customTypes, setCustomTypes] = useState<CustomAgentType[]>([]);



  // editing state
  const [isEditing, setIsEditing] = useState(false);
  const [editOriginalTypeName, setEditOriginalTypeName] = useState<string | null>(null);


// ...inside your component:
const [confirmOpen, setConfirmOpen] = useState(false);
const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
const [deleting, setDeleting] = useState(false);

const [saving, setSaving] = useState(false);

// server-backed listing
const [serverTypes, setServerTypes] = useState<AgentServer[]>([]);
const [loading, setLoading] = useState(false);
const [page, setPage] = useState(1);
const [limit, setLimit] = useState(10);
const [total, setTotal] = useState(0);
const [totalPages, setTotalPages] = useState(1);

// server filters (align to your endpoint)
const [onlyActive, setOnlyActive] = useState<boolean | undefined>(true);
const [includeDeleted, setIncludeDeleted] = useState(false);
const [searchQuery, setSearchQuery] = useState('');



const refreshList = async () => {
  setLoading(true);
  try {
    const data =
      await agentService.listAgentTypes({
        
        page,
        limit,
        search: searchQuery || "",
        isActive: typeof onlyActive === "boolean" ? onlyActive : undefined,
        includeDeleted,
      });

    // data may be { data: {...} } or direct; normalize:
    const payload: PaginatedAgents = (data as any)?.agentTypes
      ? (data as any)
      : (data as any)?.data ?? (data as any);

    setServerTypes(payload.agentTypes || []);
    setTotal(payload.total || 0);
    setTotalPages(payload.totalPages || 1);
  } catch (e) {
    console.error("Failed to load agent types:", e);
  } finally {
    setLoading(false);
  }
};

useEffect(() => {
  let t = setTimeout(() => {
    refreshList();
  }, 300);
  return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [page, limit, onlyActive, includeDeleted, searchQuery]);


const serverByName = useMemo(() => {
  const m = new Map<string, AgentServer>();
  for (const s of serverTypes) m.set((s.name || "").trim(), s);
  return m;
}, [serverTypes]);

const serverByCode = useMemo(() => {
  const m = new Map<string, AgentServer>();
  for (const s of serverTypes) m.set((s.code || "").trim(), s);
  return m;
}, [serverTypes]);
const [deleteId, setDeleteId] = useState<string | null>(null);
const [editId, setEditId] = useState<string | null>(null);


const askDelete = (id: string) => {
  setDeleteId(id);
  setConfirmOpen(true);
};

const doDelete = async () => {
  if (!deleteId) return;
  try {
    setDeleting(true);
    await agentService.deleteAgentType(deleteId);
    toast.success("Deleted successfully")

    await refreshList();
  } catch (e) {
    console.error("❌ Server delete failed:", e);
  } finally {
    setDeleting(false);
    setConfirmOpen(false);
    setDeleteId(null);
  }
};




  // Load custom types from localStorage on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem('customAgentTypes');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          const normalized: CustomAgentType[] = parsed
            .filter((t: any) => typeof t?.type === 'string')
            .map((t: any) => ({
              type: t.type,
              description: t.description,
              code: t.code || '',
              name: t.name || t.type,
              active: typeof t.active === 'boolean' ? t.active : true,
              capabilities: Array.isArray(t.capabilities) ? t.capabilities : [],
              policies: Array.isArray(t.policies) ? t.policies : [],
              metadata: t.metadata,
            }));
          setCustomTypes(normalized);
        }
      }
    } catch {
   
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('customAgentTypes', JSON.stringify(customTypes));
    } catch {
      
    }
  }, [customTypes]);

  const typeToCount = useMemo(() => {
    const counts = new Map<string, number>();
    for (const agent of AI_AGENTS_DATA) {
      const current = counts.get(agent.type) || 0;
      counts.set(agent.type, current + 1);
    }
    return counts;
  }, []);


  const customByType = useMemo(() => {
    const m = new Map<string, CustomAgentType>();
    for (const ct of customTypes) m.set(ct.type, ct);
    return m;
  }, [customTypes]);

  const agentTypes = useMemo(() => {
    const builtIns = new Map<string, number>(typeToCount);
    const merged = new Map<
      string,
      {
        count: number;
        isCustom: boolean;
        description?: string;
        hasOverlay: boolean;
        hasServer: boolean;
        serverId?: string;
        active?: boolean;
      }
    >();

    builtIns.forEach((count, type) => {
      const overlay = customByType.get(type);
      const server = serverByName.get(type);
      merged.set(type, {
        count,
        isCustom: false,
        description: overlay?.description ?? server?.description ?? undefined,
        hasOverlay: !!overlay,
        hasServer: !!server,
        serverId: server?.id,
        active: server?.isActive,
      });
    });
  
    
    for (const t of customTypes) {
      const key = t.type;
      const prev = merged.get(key);
      const server = serverByName.get(key);
      if (!prev) {
        merged.set(key, {
          count: 0,
          isCustom: true,
          description: t.description ?? server?.description ?? undefined,
          hasOverlay: true,
          hasServer: !!server,
          serverId: server?.id,
          active: server?.isActive ?? t.active,
        });
      } else {
        merged.set(key, {
          ...prev,
          description: t.description ?? prev.description,
          hasOverlay: true,
          hasServer: prev.hasServer || !!server,
          serverId: prev.serverId || server?.id,
          active: prev.active ?? server?.isActive,
        });
      }
    }
  
  
    for (const s of serverTypes) {
      const key = (s.name || "").trim();
      if (!merged.has(key)) {
        merged.set(key, {
          count: 0,
          isCustom: false,
          description: s.description ?? undefined,
          hasOverlay: false,
          hasServer: true,
          serverId: s.id,
          active: s.isActive,
        });
      }
    }
  
    return Array.from(merged.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([type, meta]) => ({
        type,
        count: meta.count,
        isCustom: meta.isCustom,
        description: meta.description,
        hasOverlay: meta.hasOverlay,
        hasServer: meta.hasServer,
        serverId: meta.serverId,
        active: meta.active,
      }));
  }, [typeToCount, customTypes, customByType, serverTypes, serverByName]);
  
  const resetForm = () => {
    setNewTypeCode('');
    setNewTypeName('');
    setNewTypeDescription('');
    setNewTypeActive(true);
    setNewTypeCapabilities([]);
    setNewTypePolicies([]);
    setNewTypeMetadata('');
    setError(null);
    setIsEditing(false);
    setEditOriginalTypeName(null);
  };

  const openCreateModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const openEditModal = (agt: AgentServer) => {
    setEditId(agt?.id);
    setError(null);
    setIsEditing(true);
  
    setEditOriginalTypeName(agt.name || agt.code);
  
    setNewTypeCode(agt.code || '');
    setNewTypeName(agt.name || '');
    setNewTypeDescription(agt.description || '');
    setNewTypeActive(!!agt.isActive);
  
 
    const caps = (agt.capabilities || []).map((c: any) => {
      if (typeof c === 'string') return c;
      const code = c.capabilityCode || c.code || c.name || '';
      const cfg = c.config && Object.keys(c.config).length ? `:${JSON.stringify(c.config)}` : '';
      return `${code}${cfg}`;
    });
    const pols = (agt.policies || []).map((p: any) => {
      if (typeof p === 'string') return p;
      const code = p.policyCode || p.code || p.name || '';
      const rules = p.rules && Object.keys(p.rules).length ? `:${JSON.stringify(p.rules)}` : '';
      return `${code}${rules}`;
    });
  
    setNewTypeCapabilities(caps);
    setNewTypePolicies(pols);
  
    setNewTypeMetadata(agt.metadata ? JSON.stringify(agt.metadata, null, 2) : '');
  
    setIsModalOpen(true);
  };
  
  

  const handleDeleteOverlay = (typeName: string) => {
  
    setCustomTypes(prev => prev.filter(ct => ct.type !== typeName));
  };

  const handleCreateOrUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
  
    const code = newTypeCode.trim().toUpperCase();
    const name = newTypeName.trim();
    const desc = newTypeDescription.trim();
  
    if (!code) {
      setError("Code is required.");
      return;
    }

    if (!name) {
      setError("Type name is required.");
      return;
    }
  
    let metadataObj: any = undefined;
    if (newTypeMetadata.trim()) {
      try {
        metadataObj = JSON.parse(newTypeMetadata);
      } catch {
        setError("Invalid JSON in metadata.");
        return;
      }
    }
  
    const cleanedCapabilities = Array.from(
      new Set(newTypeCapabilities.map((c) => c.trim()).filter(Boolean))
    );
    const cleanedPolicies = Array.from(
      new Set(newTypePolicies.map((p) => p.trim()).filter(Boolean))
    );
  
    // ------- Build payloads -------
    const consolePayload = {
      code,
      name,
      type: name,
      description: desc || undefined,
      active: newTypeActive,
      capabilities: cleanedCapabilities,
      policies: cleanedPolicies,
      metadata: metadataObj,
    };
  
    const serverPayload = toServerPayload({
      code,
      name,
      desc,
      active: newTypeActive,
      capabilities: cleanedCapabilities,
      policies: cleanedPolicies,
      metadataObj,
    });
  

    // ------- Optimistic local update (keep cards instant) -------
    const newItem: CustomAgentType = {
      type: name,
      name,
      code,
      description: desc || undefined,
      active: newTypeActive,
      capabilities: cleanedCapabilities,
      policies: cleanedPolicies,
      metadata: metadataObj,
    };
  
    if (isEditing && editOriginalTypeName) {
      setCustomTypes((prev) => {
        const idx = prev.findIndex(
          (ct) => ct.type.toLowerCase() === editOriginalTypeName.toLowerCase()
        );
        if (idx === -1) return [...prev, newItem];
        const copy = [...prev];
        copy[idx] = newItem;
        return copy;
      });
    } else {
      setCustomTypes((prev) => [...prev, newItem]);
    }
  
    setSaving(true);
    try {
      if (isEditing && editOriginalTypeName) {
        const overlay = customByType.get(editOriginalTypeName);
        const id =
          (overlay?.metadata && (overlay.metadata.id || overlay.metadata._id)) ||
          code;

          console.log(id,"Edit")
  
        const res = await agentService.updateAgentType(String(editId), serverPayload);
        toast.success("Updated successfully")
        await refreshList()
      } else {
        const res = await agentService.createAgentType(serverPayload);
        toast.success("Created successfully")
        await refreshList()

        if (res && (res as any).id) {
          setCustomTypes((prev) =>
            prev.map((ct) =>
              ct.code === code
                ? {
                    ...ct,
                    metadata: {
                      ...(ct.metadata || {}),
                      id: (res as any).id,
                    },
                  }
                : ct
            )
          );
        } else if (res && (res as any)._id) {
          setCustomTypes((prev) =>
            prev.map((ct) =>
              ct.code === code
                ? {
                    ...ct,
                    metadata: {
                      ...(ct.metadata || {}),
                      _id: (res as any)._id,
                    },
                  }
                : ct
            )
          );
        }
      }
    } catch (err) {
      console.error("❌ Save failed:", err);

    } finally {
      setSaving(false);
    }

    setIsModalOpen(false);
    resetForm();
  };
  
  

useEffect(()=>{
console.log(searchQuery)
},[searchQuery])
  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-white/30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between gap-4">
            {/* Left: Title + subtitle */}
            <div className="min-w-0">
              <h1 className="text-3xl font-bold bg-gradient-to-r from-agentic-text-primary via-agentic-primary-blue to-agentic-accent-purple bg-clip-text text-transparent">
                Agent Types
              </h1>
              <p className="mt-2 text-agentic-text-secondary">
                Browse all available agent types in your workspace.
              </p>
            </div>

            {/* Right: Search + New button */}
            <div className="flex items-center gap-3 w-full max-w-xl justify-end">
              {/* NEW: Search input */}
              <div className="relative flex-1 max-w-sm">
                <input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search agent types…"
                  className="w-full pl-10 pr-3 py-2 rounded-xl border border-gray-200 focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
                />
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔎</span>
              </div>
            </div>
          </div>
        </div>
      </div>


<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
  {loading ? (
    <div className="text-center py-16">Loading…</div>
  ) : serverTypes.length === 0 ? (
    <div className="text-center py-16">
      <div className="text-6xl mb-4">🏷️</div>
      <h3 className="text-xl font-semibold text-agentic-text-primary mb-2">
        No agent types found
      </h3>
      <p className="text-agentic-text-secondary">Create an agent type to get started.</p>
    </div>
  ) : (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {serverTypes.map((agt) => (
        <Card
          key={agt.id}
          hover
          className="rounded-2xl"
          title={
            <div className="flex items-center justify-between">
              <span>{agt.name || agt.code}</span>
              <div className="flex items-center gap-2">
                {/* <button
                  type="button"
                  onClick={() => openEditModal(agt)}
                  className="px-2 py-1 text-sm rounded-lg border border-gray-200 hover:bg-gray-50"
                  title="Edit"
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => askDelete(agt.id)}
                  className="px-2 py-1 text-sm rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
                >
                  Delete
                </button> */}
              </div>
            </div>
          }
          subtitle={`${agt.code} • ${agt.isActive ? 'Active' : 'Inactive'}`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-agentic-primary-blue to-agentic-primary-blue-light text-white flex items-center justify-center">
                <span className="text-lg">🏷️</span>
              </div>
              <div className="text-sm text-agentic-text-secondary">
                {agt.description ? (
                  <span>{agt.description}</span>
                ) : (
                  <span>Created {new Date(agt.createdAt || '').toLocaleString()}</span>
                )}
              </div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  )}
</div>

      {/* Create / Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 w-[30vw]">
          <div className="absolute inset-0 bg-black/30" onClick={() => { setIsModalOpen(false); resetForm(); }}></div>
          <div className="relative w-full max-w-4xl mx-4 bg-white rounded-2xl shadow-xl border border-gray-100 p-6 max-h-[85vh] overflow-y-auto">
            <div className="mb-4">
              <h2 className="text-xl font-semibold text-agentic-text-primary">
                {isEditing ? 'Edit Agent Type' : 'Create Agent Type'}
              </h2>
              <p className="text-sm text-agentic-text-secondary mt-1">
                {isEditing ? 'Update the agent type details.' : 'Define a new agent type identifier.'}
              </p>
            </div>
            {error && (
              <div className="mb-3 px-3 py-2 rounded-lg text-sm bg-red-50 text-red-700 border border-red-200">{error}</div>
            )}
            <form onSubmit={handleCreateOrUpdate} className="space-y-6">
              {/* Code + Name */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-agentic-text-primary mb-1">
                    Code<span className="text-red-500"> *</span>
                  </label>
                  <input
                    value={newTypeCode}
                    onChange={(e) => setNewTypeCode(e.target.value.toUpperCase())}
                    placeholder="RESEARCHER"
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
                  />
                  <p className="text-xs text-agentic-text-muted mt-1">Uppercase, digits, underscore. 2–40 chars.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-agentic-text-primary mb-1">
                    Name<span className="text-red-500"> *</span>
                  </label>
                  <input
                    value={newTypeName}
                    onChange={(e) => setNewTypeName(e.target.value)}
                    placeholder="Research Agent"
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-agentic-text-primary mb-1">Description</label>
                <textarea
                  value={newTypeDescription}
                  onChange={(e) => setNewTypeDescription(e.target.value)}
                  placeholder="Short description of what agents of this type generally do"
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
                  rows={4}
                />
              </div>

              {/* Status */}
              <div className="flex items-center justify-between py-2 border rounded-lg px-3 border-gray-200">
                <div>
                  <div className="text-sm font-medium text-agentic-text-primary">Status</div>
                  <div className="text-xs text-agentic-text-secondary">Disable to keep the type unavailable until ready.</div>
                </div>
                <label className="inline-flex items-center space-x-2">
                  <input type="checkbox" checked={newTypeActive} onChange={(e) => setNewTypeActive(e.target.checked)} />
                  <span className="text-sm text-agentic-text-primary">Active</span>
                </label>
              </div>

              {/* Capabilities */}
              <div>
                <div className="text-sm font-medium text-agentic-text-primary mb-2">Capabilities</div>
                {newTypeCapabilities.length === 0 && (
                  <p className="text-sm text-agentic-text-secondary mb-2">No capabilities yet. Click Add to define one.</p>
                )}
                <div className="space-y-2 mb-2">
                  {newTypeCapabilities.map((cap, idx) => (
                    <div key={`cap-${idx}`} className="flex items-center gap-2">
                      <input
                        value={cap}
                        onChange={(e) => {
                          const v = e.target.value;
                          setNewTypeCapabilities((prev) => prev.map((c, i) => (i === idx ? v : c)));
                        }}
                        placeholder="e.g., web-search"
                        className="flex-1 px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
                      />
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          setNewTypeCapabilities((prev) => prev.filter((_, i) => i !== idx));
                        }}
                        className="px-3 py-2 rounded-lg border border-gray-200 text-red-600 hover:bg-red-50"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setNewTypeCapabilities((prev) => [...prev, '']);
                  }}
                  className="px-3 py-2 rounded-lg border border-gray-200 text-agentic-text-primary hover:bg-gray-50"
                >
                  + Add Capability
                </button>
              </div>

           


              {/* Policies */}
              <div>
                <div className="text-sm font-medium text-agentic-text-primary mb-2">Policies</div>
                {newTypePolicies.length === 0 && (
                  <p className="text-sm text-agentic-text-secondary mb-2">No policies yet. Click Add to define one.</p>
                )}
                <div className="space-y-2 mb-2">
                  {newTypePolicies.map((pol, idx) => (
                    <div key={`pol-${idx}`} className="flex items-center gap-2">
                      <input
                        value={pol}
                        onChange={(e) => {
                          const v = e.target.value;
                          setNewTypePolicies((prev) => prev.map((p, i) => (i === idx ? v : p)));
                        }}
                        placeholder="e.g., no-sensitive-data"
                        className="flex-1 px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
                      />
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          setNewTypePolicies((prev) => prev.filter((_, i) => i !== idx));
                        }}
                        className="px-3 py-2 rounded-lg border border-gray-200 text-red-600 hover:bg-red-50"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setNewTypePolicies((prev) => [...prev, '']);
                  }}
                  className="px-3 py-2 rounded-lg border border-gray-200 text-agentic-text-primary hover:bg-gray-50"
                >
                  + Add Policy
                </button>
              </div>

              {/* Metadata JSON */}
              <div>
                <label className="block text-sm font-medium text-agentic-text-primary mb-1">Metadata (JSON)</label>
                <textarea
                  value={newTypeMetadata}
                  onChange={(e) => setNewTypeMetadata(e.target.value)}
                  placeholder='{"maxConcurrency": 4}'
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent font-mono text-sm"
                  rows={5}
                />
              </div>

              <div className="flex items-center justify-end space-x-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setIsModalOpen(false); resetForm(); }}
                  className="px-4 py-2 rounded-lg border border-gray-200 text-agentic-text-secondary hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg text-white bg-gradient-to-r from-agentic-primary-blue to-agentic-primary-blue-light hover:shadow-md"
                >
                  {isEditing ? 'Save Changes' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmationDialog
        isOpen={confirmOpen}
        onClose={() => { setConfirmOpen(false); setDeleteId(null); }}
        onConfirm={doDelete}
        title="Confirm Delete"
        message="Are you sure you want to delete this agent type?"
        confirmText="Delete"
        cancelText="Cancel"
        loading={deleting}
        variant="error"
      />


    </div>
  );
};
